import { useEffect, useMemo, useState, useRef } from "react";
import Input from "../common/Input";
import { useDispatch, useSelector } from "react-redux";
import { searchCustomerByNumber, savePatientDetails } from "../../redux/Thunk/AdminAccount.js/AccountThunk";
import toast from "react-hot-toast";
import debounce from "lodash/debounce";
import { memo } from "react";
import pincodeData from "../../utils/pincodeCityDirectory.json";

const PatientOrderDetail = ({
  handleChange,
  values,
  setFieldValue,
  selectedDoctor,
  setSelectedDoctor,
  touched,
  errors,
  setFieldTouched,
  handleBlur,
  readOnly,
}) => {
  const doctors = useSelector(
    (state) => state?.doctor?.doctorAssociatedWithStore
  );
  const role = useSelector((state) => state?.auth?.role);

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // New states for Pincode/City flow
  const [cityMode, setCityMode] = useState(false);
  const [ageMode, setAgeMode] = useState(false);
  const [pincodeSuggestions, setPincodeSuggestions] = useState([]);
  const [showPincodeSuggestions, setShowPincodeSuggestions] = useState(false);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);

  const pincodeRef = useRef(null);
  const cityRef = useRef(null);

  const [isSaving, setIsSaving] = useState(false);
  const [isExistingPatient, setIsExistingPatient] = useState(false);
  const [initialFetchedValues, setInitialFetchedValues] = useState(null);

  const dispatch = useDispatch();

  const debouncedHandlePhoneNumberChange = useMemo(
    () =>
      debounce(async (phoneNumber) => {
        if (phoneNumber?.length >= 3 && phoneNumber?.length <= 10) {
          // Fetch matching phone numbers
          try {
            const response = await dispatch(
              searchCustomerByNumber(phoneNumber)
            ).unwrap();
            const data = response;

            if (data && data?.length > 0) {
              setSuggestions(data); // Set matching phone numbers
              setShowSuggestions(true); // Show the dropdown
            } else {
              setSuggestions([]);
              setShowSuggestions(false);
            }
          } catch (error) {
            console.error("Error fetching phone numbers", error);
          }
        } else {
          setSuggestions([]);
          setShowSuggestions(false); // Hide suggestions if input is cleared or too short
        }
      }, 300),
    [dispatch]
  );

  useEffect(() => {
    return () => {
      debouncedHandlePhoneNumberChange.cancel();
    };
  }, [debouncedHandlePhoneNumberChange]);

  // Buggy useEffect that overrides selectedDoctor removed.

  // Handle closing dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pincodeRef.current && !pincodeRef.current.contains(event.target)) {
        setShowPincodeSuggestions(false);
      }
      if (cityRef.current && !cityRef.current.contains(event.target)) {
        setShowCitySuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Completion Logic
  const isNameFilled = values?.patient?.name?.trim()?.length > 0;
  const isSurnameFilled = values?.patient?.surname?.trim()?.length > 0;
  const isPhoneFilled = values?.patient?.phoneNumber?.length >= 10;
  const isGenderFilled = values?.patient?.gender?.length > 0;
  const isPincodeFilled = values?.patient?.pincode?.length === 6;
  const isCityFilled = values?.patient?.city?.trim()?.length > 0;
  const isDobFilled = !!values?.patient?.dateofbirth;
  const isAgeFilled = String(values?.patient?.age)?.trim()?.length > 0;

  const isLocationFilled = cityMode ? isCityFilled : isPincodeFilled;
  const isAgeMetricFilled = ageMode ? isAgeFilled : isDobFilled;

  const isAllFilled = isPhoneFilled && isNameFilled && isSurnameFilled && isGenderFilled && isLocationFilled && isAgeMetricFilled;

  const getPendingFields = () => {
    const pending = [];
    if (!isPhoneFilled) pending.push("Contact");
    if (!isNameFilled || !isSurnameFilled) pending.push("Name");
    if (!isGenderFilled) pending.push("Gender");
    if (!isLocationFilled) pending.push(cityMode ? "City" : "Pincode");
    if (!isAgeMetricFilled) pending.push(ageMode ? "Age" : "DoB");
    return pending;
  };

  const getPendingStyle = (isFilled, currentError) => {
    if (currentError) return ""; // Let error style take priority
    if (!isFilled) return "ring-1 ring-primary-300 border-primary-300 bg-white";
    return "";
  };

  // Sync modes with loaded data (initial mount for pre-fills)
  useEffect(() => {
    if (values?.patient?.city && !values?.patient?.pincode) {
      setCityMode(true);
    }
    if (values?.patient?.age && !values?.patient?.dateofbirth) {
      setAgeMode(true);
    }
    // If PID is present from external load, mark as existing
    if (values?.patient?.PID) {
      setIsExistingPatient(true);
      if (!initialFetchedValues) {
        setInitialFetchedValues({ ...values.patient });
      }
    } else {
      setIsExistingPatient(false);
      setInitialFetchedValues(null);
    }
  }, [values?.patient?.PID, values?.patient, initialFetchedValues]); // Run when PID changes

  const handlePhoneNumberChange = async (e) => {
    const phoneNumber = e.target.value;
    setFieldValue("patient.phoneNumber", phoneNumber);

    if (phoneNumber?.length <= 10) {
      debouncedHandlePhoneNumberChange(phoneNumber);
    }

    // If phone number changes and doesn't match fetched person, reset existing status
    if (isExistingPatient && initialFetchedValues && phoneNumber !== initialFetchedValues.phoneNumber) {
      setIsExistingPatient(false);
      setInitialFetchedValues(null);
      setFieldValue("patient.PID", null);
    }
  };

  const nameInputRef = useRef(null);
  const surnameInputRef = useRef(null);

  const handleNameKeyDown = (e) => {
    if (e.key === " " && values?.patient?.name?.trim().length > 0) {
      e.preventDefault();
      surnameInputRef.current?.focus();
    }
  };
  const handleSelectSuggestion = (e, suggestion) => {
    e.preventDefault();
    setFieldValue("patient.phoneNumber", suggestion.phoneNumber);
    setFieldValue("patient.name", suggestion.name || "");
    setFieldValue("patient.surname", suggestion.surname || "");
    setFieldValue("patient.dateofbirth", suggestion.dateofbirth || "");
    setFieldValue("patient.age", suggestion.age || "");
    setFieldValue("patient.gender", suggestion.gender || "");
    setFieldValue("patient.PID", suggestion.PID || "");

    // Autofill pincode and city with fallback to patientAddress
    const pincode = suggestion.pincode || suggestion.patientAddress?.pincode || "";
    const city = suggestion.city || suggestion.patientAddress?.city || "";
    setFieldValue("patient.pincode", pincode);
    setFieldValue("patient.city", city);

    // Toggle swappable modes
    if (city && !pincode) {
      setCityMode(true);
    } else {
      setCityMode(false);
    }

    if (suggestion.age && !suggestion.dateofbirth) {
      setAgeMode(true);
    } else {
      setAgeMode(false);
    }

    if (suggestion.patientAddress) {
      setFieldValue("delivery.address.addressType", "");
      setFieldValue("delivery.address.areaRoad", suggestion.patientAddress?.areaRoad);
      setFieldValue("delivery.address.city", suggestion.patientAddress?.city);
      setFieldValue("delivery.address.pincode", suggestion.patientAddress?.pincode);
      setFieldValue("delivery.address.premisesName", suggestion.patientAddress?.premisesName);
      setFieldValue("delivery.address.premisesNoFloor", suggestion.patientAddress?.premisesNoFloor);
      setFieldValue("delivery.address.state", suggestion.patientAddress?.state);
      setFieldValue("delivery.address.landmark", suggestion.patientAddress?.landmark);
      setFieldValue("delivery.address.phoneNumber2", suggestion.patientAddress?.phoneNumber2);
    }

    setIsExistingPatient(true);
    setInitialFetchedValues({
      phoneNumber: suggestion.phoneNumber || "",
      name: suggestion.name || "",
      surname: suggestion.surname || "",
      gender: suggestion.gender || "",
      dateofbirth: suggestion.dateofbirth || "",
      age: suggestion.age || "",
      pincode: pincode,
      city: city,
    });

    setFieldTouched("patient.phoneNumber", false);
    setShowSuggestions(false);
  };

  const handlePincodeChange = (e) => {
    const query = e.target.value;
    setFieldValue("patient.pincode", query);
    setFieldValue("patient.city", "");
    setFieldValue("delivery.address.pincode", query);

    if (query.trim().length >= 3) {
      const matched = pincodeData.filter(d => String(d.pincode).startsWith(query)).slice(0, 100);
      setPincodeSuggestions(matched);
      setShowPincodeSuggestions(true);
    } else {
      setShowPincodeSuggestions(false);
    }
  };

  const handleSelectPincode = (item) => {
    setFieldValue("patient.pincode", item.pincode);
    setFieldValue("delivery.address.pincode", item.pincode);
    setFieldValue("patient.city", item.city || "");
    setFieldValue("delivery.address.city", item.city || "");
    setFieldValue("delivery.address.state", item.state || "");
    setShowPincodeSuggestions(false);
  };

  const handleCityChange = (e) => {
    const query = e.target.value;
    setFieldValue("patient.city", query);
    setFieldValue("patient.pincode", "");
    setFieldValue("delivery.address.city", query);

    if (query.trim().length >= 3) {
      const matched = [];
      const seen = new Set();
      for (const d of pincodeData) {
        if (d.city && d.city.toLowerCase().includes(query.toLowerCase())) {
          const key = `${d.city}-${d.state}`;
          if (!seen.has(key)) {
            seen.add(key);
            matched.push(d);
            if (matched.length >= 20) break;
          }
        }
      }
      setCitySuggestions(matched);
      setShowCitySuggestions(true);
    } else {
      setShowCitySuggestions(false);
    }
  };

  const handleSelectCity = (item) => {
    setFieldValue("patient.city", item.city);
    setFieldValue("delivery.address.city", item.city);
    setFieldValue("delivery.address.state", item.state || "");
    setFieldValue("patient.pincode", "");
    setFieldValue("delivery.address.pincode", "");
    setShowCitySuggestions(false);
  };

  const handleToggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleSelectDoctor = (e, doctor) => {
    e.preventDefault();
    setFieldValue("DID", doctor?.DID);
    setSelectedDoctor(doctor);
    setIsOpen(false);
  };

  const [showGenderDropdown, setShowGenderDropdown] = useState(false);
  const genderRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pincodeRef.current && !pincodeRef.current.contains(event.target)) {
        setShowPincodeSuggestions(false);
      }
      if (cityRef.current && !cityRef.current.contains(event.target)) {
        setShowCitySuggestions(false);
      }
      if (genderRef.current && !genderRef.current.contains(event.target)) {
        setShowGenderDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  return (
    <div className="w-full border border-primary-100 bg-white p-4 rounded-[12px] min-h-[108px]">
      <div className="flex flex-col gap-x-4">
        {role === "store" ? (
          <div className="w-full flex-shrink-0 relative mb-2">
            <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-1.5">Prescribing Doctor</p>
            <div
              onClick={() => { if (!readOnly) handleToggleDropdown(); }}
              className={`group relative px-3 py-2.5 rounded-[10px] border-2 h-[56px] flex items-center justify-between mb-4 transition-all duration-200 ${readOnly
                ? 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-70'
                : isOpen
                  ? 'border-primary-400 bg-white shadow-md ring-2 ring-primary-100'
                  : Object.keys(selectedDoctor)?.length > 0
                    ? 'border-primary-100 bg-gradient-to-r from-primary-50/40 to-white hover:border-primary-300 cursor-pointer'
                    : 'border-dashed border-gray-300 bg-gray-50/50 hover:border-primary-300 hover:bg-white cursor-pointer'
                }`}
            >
              {Object.keys(selectedDoctor)?.length > 0 ? (
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${selectedDoctor?.currentDoctorStatus === "ACTIVE" ? "bg-green-500 shadow-[0_0_4px_rgba(34,197,94,0.5)]"
                      : selectedDoctor?.currentDoctorStatus === "AWAY" ? "bg-amber-500 shadow-[0_0_4px_rgba(245,158,11,0.5)]"
                        : selectedDoctor?.currentDoctorStatus === "CLOSED" ? "bg-red-500"
                          : "bg-gray-400"
                      }`}></span>
                    <p className="text-[13px] font-semibold text-gray-800 truncate leading-tight">
                      Dr. {selectedDoctor?.personalInfo?.name || selectedDoctor?.doctor?.personalInfo?.name || ""}{" "}
                      {selectedDoctor?.personalInfo?.surname || selectedDoctor?.doctor?.personalInfo?.surname || ""}
                    </p>
                  </div>
                  <p className="text-[10px] text-gray-400 truncate leading-tight mt-0.5 pl-3.5">
                    {selectedDoctor?.DID} • {selectedDoctor?.personalInfo?.clinicName || selectedDoctor?.doctor?.personalInfo?.clinicName || ""}
                  </p>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-gray-400">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  <span className="text-[13px] font-medium">Select Doctor</span>
                </div>
              )}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                className={`flex-shrink-0 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''} ${!readOnly ? 'group-hover:text-primary-500' : ''}`}
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </div>

            {/* Dropdown */}
            {isOpen && (
              <div className="absolute z-[999] w-full mt-1.5 bg-white border border-gray-200 rounded-[12px] shadow-xl overflow-hidden">
                {doctors[0]?.doctors?.length > 0 ? (
                  <ul className="max-h-[240px] overflow-y-auto py-1">
                    {doctors[0]?.doctors?.map((doctor) => {
                      const isSelected = selectedDoctor?.DID === doctor?.DID;
                      return (
                        <li
                          key={doctor?.DID}
                          onClick={(e) => {
                            e.preventDefault();
                            handleSelectDoctor(e, doctor);
                          }}
                          className={`px-3 py-2.5 cursor-pointer transition-all duration-150 mx-1 my-0.5 rounded-[8px] ${isSelected
                            ? 'bg-primary-50 ring-1 ring-primary-200'
                            : 'hover:bg-gray-50'
                            }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5">
                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${doctor?.currentDoctorStatus === "ACTIVE" ? "bg-green-500"
                                  : doctor?.currentDoctorStatus === "AWAY" ? "bg-amber-500"
                                    : doctor?.currentDoctorStatus === "CLOSED" ? "bg-red-500"
                                      : "bg-gray-400"
                                  }`}></span>
                                <p className={`text-[13px] font-semibold truncate leading-tight ${isSelected ? 'text-primary-700' : 'text-gray-800'}`}>
                                  Dr. {doctor?.personalInfo?.name} {doctor?.personalInfo?.surname}
                                </p>
                              </div>
                              <p className="text-[10px] text-gray-400 truncate leading-tight mt-0.5 pl-3.5">
                                {doctor?.DID} • {doctor?.personalInfo?.clinicName || ""}
                              </p>
                            </div>
                            {isSelected && (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0369a1" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div className="px-4 py-6 text-center">
                    <p className="text-[13px] text-gray-400 font-medium">No doctors linked</p>
                    <p className="text-[11px] text-gray-300 mt-0.5">Link doctors to this store first</p>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : null}
        <div
          className="flex-1 flex flex-col gap-y-3"
        >
          {/* Consistent Header */}
          <div className="w-full">
            <div className="flex flex-wrap items-center p-1 bg-primary-50 border-[0.5px] border-primary-100 rounded-[8px] w-full shadow-sm relative gap-1">
              <div className="hidden sm:flex p-1">
                <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-[4px]">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill={isAllFilled ? "#00C732" : "#0ea5e9"} xmlns="http://www.w3.org/2000/svg">
                    {isAllFilled ? (
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#00C732" />
                    ) : (
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="#0ea5e9" />
                    )}
                  </svg>
                  <h3 className="text-[15px] font-semibold text-secondry-600 whitespace-nowrap uppercase">
                    Patient Details
                  </h3>
                </div>
              </div>
              <div className="hidden sm:block w-[1px] h-5 bg-primary-100 mx-1"></div>
              <div className={`hidden md:block px-3 text-[12px] font-medium transition-all duration-300 ${isAllFilled ? "text-success-600" : "text-primary-400"}`}>
                {isAllFilled ? (
                  <span className="flex items-center gap-1">Everything looks good!</span>
                ) : (
                  <span>Enter patient details {getPendingFields().length > 0 && <span className="opacity-70">(Pending: {getPendingFields().join(", ")})</span>}</span>
                )}
              </div>

              {/* Save/Update Button Container */}
              <div className="flex-1 md:flex-none flex items-center justify-end gap-1.5 p-1 ml-auto">
                {(() => {
                  const hasChanges = isExistingPatient && initialFetchedValues && (
                    values.patient.name !== initialFetchedValues.name ||
                    values.patient.surname !== initialFetchedValues.surname ||
                    values.patient.gender !== initialFetchedValues.gender ||
                    values.patient.phoneNumber !== initialFetchedValues.phoneNumber ||
                    values.patient.pincode !== initialFetchedValues.pincode ||
                    values.patient.city !== initialFetchedValues.city ||
                    String(values.patient.age) !== String(initialFetchedValues.age || "") ||
                    values.patient.dateofbirth !== (initialFetchedValues.dateofbirth || "")
                  );

                  const buttonText = isExistingPatient ? (hasChanges ? "Update" : "Save") : "Save";
                  const isDisabled = !isAllFilled || (isExistingPatient && !hasChanges) || isSaving;

                  return (
                    <div className="w-full sm:w-auto flex items-center gap-2 sm:gap-1.5 overflow-x-auto no-scrollbar min-w-0 flex-1 justify-end">
                      <button
                        type="button"
                        disabled={isDisabled}
                        onClick={async () => {
                          setIsSaving(true);
                          try {
                            const payload = {
                              patient: values.patient,
                              DID: selectedDoctor?.DID || selectedDoctor?.doctor?.DID || ""
                            };
                            if (!payload.DID) {
                              toast.error("Please select a Doctor first before saving patient details");
                              setIsSaving(false);
                              return;
                            }
                            const response = await dispatch(savePatientDetails(payload)).unwrap();
                            const savedPatient = response.data;

                            toast.success(response.message || "Patient details saved successfully");

                            // Update Formik state with the new/existing PID from server
                            if (savedPatient?.PID) {
                              setFieldValue("patient.PID", savedPatient.PID);
                            }

                            // Update initial values after save to reset hasChanges
                            setIsExistingPatient(true);
                            setInitialFetchedValues({
                              phoneNumber: savedPatient?.phoneNumber || values.patient.phoneNumber,
                              name: savedPatient?.name || values.patient.name,
                              surname: savedPatient?.surname || values.patient.surname,
                              gender: savedPatient?.gender || values.patient.gender,
                              dateofbirth: savedPatient?.dateofbirth || values.patient.dateofbirth,
                              age: savedPatient?.age || values.patient.age,
                              pincode: savedPatient?.pincode || values.patient.pincode,
                              city: savedPatient?.city || values.patient.city,
                            });
                          } catch (err) {
                            toast.error(err.message || "Failed to save details");
                          } finally {
                            setIsSaving(false);
                          }
                        }}
                        className={`flex-1 sm:flex-initial text-center justify-center px-3 md:px-4 py-1.5 rounded-[6px] text-[11px] md:text-[12px] font-semibold transition-all duration-300 whitespace-nowrap ${isDisabled
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200"
                          : "bg-success-50 text-success-700 hover:bg-success-600 hover:text-white border border-success-200 shadow-sm"
                          }`}
                      >
                        {isSaving ? "Saving..." : buttonText}
                      </button>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          <div className="w-full">
            <div className="grid grid-cols-12 gap-4">
              {/* Row 1: Contact, Full Name, Gender */}
              <div className="col-span-12 md:col-span-4 relative">
                <Input
                  name="patient.phoneNumber"
                  value={values.patient.phoneNumber}
                  onChange={handlePhoneNumberChange}
                  placeholder="Enter Contact"
                  type="number"
                  labelName="Contact"
                  labelClassName="block text-primary-400 text-[13px] leading-[20px]"
                  className="border-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none bg-transparent shadow-none outline-none placeholder:text-gray-400 w-full h-[24px]"
                  onFocus={() => { if (!readOnly) setFieldTouched("patient.phoneNumber", true); }}
                  readOnly={readOnly}
                  inputBox={getPendingStyle(isPhoneFilled, touched.patient?.phoneNumber && errors.patient?.phoneNumber)}
                  isError={
                    touched.patient?.phoneNumber &&
                    Boolean(errors.patient?.phoneNumber)
                  }
                  errorMessage={errors.patient?.phoneNumber}
                />

                {showSuggestions && (
                  <ul className="absolute z-[9999] top-full left-0 w-full bg-white border border-gray-200 shadow-md max-h-40 overflow-y-auto rounded-md mt-1">
                    {suggestions?.map((suggestion) => (
                      <li
                        key={suggestion.phoneNumber}
                        onClick={(e) => handleSelectSuggestion(e, suggestion)}
                        className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-[13px]"
                      >
                        <div className="font-medium text-gray-800 flex items-center justify-between">
                          <span>{suggestion?.name} {suggestion?.surname}</span>
                          <span className="text-[11px] font-normal text-primary-400 bg-primary-50 px-1 rounded uppercase">
                            {suggestion?.gender?.charAt(0) || "-"} | {suggestion?.age || (suggestion?.dateofbirth ? (new Date().getFullYear() - new Date(suggestion.dateofbirth).getFullYear()) : "-")}y
                          </span>
                        </div>
                        <div className="text-gray-500 flex items-center justify-between">
                          <span>{suggestion?.phoneNumber}</span>
                          <span className="text-[11px] text-primary-300 italic">{suggestion?.city || suggestion?.patientAddress?.city || ""}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="col-span-12 md:col-span-5">
                <div
                  className={`py-[5px] px-[16px] rounded-[6px] border h-[56px] transition-all duration-500 relative flex flex-col justify-center w-full ${(touched.patient?.name && errors.patient?.name) || (touched.patient?.surname && errors.patient?.surname)
                    ? "border-orange-500 bg-orange-50"
                    : isNameFilled && isSurnameFilled
                      ? "border-primary-50 bg-primary-50 focus-within:border-primary-500"
                      : "ring-1 ring-primary-300 border-primary-300 bg-white focus-within:border-primary-500"
                    }`}
                >
                  <label className="block text-primary-400 text-[13px] leading-[18px]">Full Name</label>
                  <div className="flex items-center h-[24px] overflow-hidden whitespace-nowrap w-full">
                    {/* Width Mirror for Name */}
                    <span
                      id="name-mirror"
                      className="absolute opacity-0 pointer-events-none text-[14px]"
                      style={{ visibility: 'hidden', whiteSpace: 'pre', position: 'absolute', top: 0, left: 0 }}
                    >
                      {values?.patient?.name || "Name"}
                    </span>
                    <input
                      ref={nameInputRef}
                      name="patient.name"
                      value={values?.patient?.name}
                      onChange={handleChange}
                      onKeyDown={handleNameKeyDown}
                      placeholder="Name"
                      readOnly={readOnly}
                      style={{
                        width: values?.patient?.name
                          ? `${Math.min(300, Math.max(40, (document.getElementById('name-mirror')?.offsetWidth || 40) + 10))}px`
                          : '45px'
                      }}
                      className={`bg-transparent outline-none text-[14px] text-secondry-600 placeholder:text-gray-400 min-w-0 transition-[width] duration-75 ${readOnly ? 'cursor-not-allowed' : ''}`}
                    />

                    <span className="text-primary-200 px-1">-</span>

                    <input
                      ref={surnameInputRef}
                      name="patient.surname"
                      value={values?.patient?.surname}
                      onChange={handleChange}
                      placeholder="Surname"
                      readOnly={readOnly}
                      className={`bg-transparent outline-none text-[14px] text-secondry-600 placeholder:text-gray-400 flex-1 min-w-0 ${readOnly ? 'cursor-not-allowed' : ''}`}
                    />
                  </div>
                  {((touched.patient?.name && errors.patient?.name) || (touched.patient?.surname && errors.patient?.surname)) && (
                    <div className="absolute left-0 -bottom-5 text-orange-800 text-[11px] uppercase font-medium">
                      {errors.patient?.name || errors.patient?.surname}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-span-12 md:col-span-3 relative" ref={genderRef}>
                <div
                  onClick={() => { if (!readOnly) setShowGenderDropdown(!showGenderDropdown); }}
                  className={`py-[5px] px-[16px] rounded-[6px] border h-[56px] transition-all duration-500 relative flex flex-col justify-center ${touched.patient?.gender && errors.patient?.gender
                    ? "border-orange-500 bg-orange-50"
                    : isGenderFilled
                      ? "border-primary-50 bg-primary-50 focus-within:border-primary-500"
                      : "ring-1 ring-primary-300 border-primary-300 bg-white"
                    } ${readOnly ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
                >
                  <label className="block text-primary-400 text-[13px] leading-[18px]">Gender</label>
                  <div className="flex items-center justify-between">
                    <span className={`text-[14px] ${values.patient.gender ? 'text-secondry-600' : 'text-gray-400'}`}>
                      {values.patient.gender || "Select"}
                    </span>
                    <img src="icons/arrow-down.svg" className={`h-5 w-5 transition-transform duration-300 ${showGenderDropdown ? 'rotate-180' : ''}`} alt="" />
                  </div>
                  {touched.patient?.gender && errors.patient?.gender && (
                    <div className="absolute left-0 -bottom-5 text-orange-800 text-[11px] uppercase font-medium">
                      {errors.patient.gender}
                    </div>
                  )}
                </div>

                {showGenderDropdown && (
                  <ul className="absolute z-[9999] top-full left-0 w-full bg-white border border-gray-200 shadow-lg rounded-md mt-1 overflow-hidden transition-all duration-200">
                    {["Male", "Female", "Other"].map((option) => (
                      <li
                        key={option}
                        onClick={() => {
                          setFieldValue("patient.gender", option);
                          setShowGenderDropdown(false);
                        }}
                        className={`px-4 py-2.5 text-[14px] cursor-pointer hover:bg-primary-50 transition-colors ${values.patient.gender === option ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'}`}
                      >
                        {option}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Row 2: Pincode/City, DoB/Age */}
              <div className="col-span-12 md:col-span-6">
                {!cityMode ? (
                  <div ref={pincodeRef} className="relative w-full">
                    <Input
                      name="patient.pincode"
                      value={values?.patient?.pincode}
                      onChange={handlePincodeChange}
                      placeholder="e.g. 400001"
                      type="number"
                      labelName="Pincode"
                      labelClassName="block text-primary-400 text-[13px] leading-[20px]"
                      className="border-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none bg-transparent shadow-none outline-none placeholder:text-gray-400 w-full h-[24px]"
                      inputBox={getPendingStyle(isPincodeFilled, touched.patient?.pincode && errors.patient?.pincode)}
                      isError={
                        touched.patient?.pincode && Boolean(errors.patient?.pincode)
                      }
                      errorMessage={errors.patient?.pincode}
                    />
                    <button
                      type="button"
                      onClick={() => setCityMode(true)}
                      className={`absolute right-2 top-1/2 -translate-y-1/2 h-8 px-2 rounded-[4px] bg-primary-50 hover:bg-primary-100 text-[11px] text-primary-600 font-medium transition-colors border border-primary-100 flex items-center gap-1 ${readOnly ? 'opacity-80' : ''}`}
                    >
                      Swap to City
                    </button>
                    {showPincodeSuggestions && (
                      <ul className="absolute z-[9999] top-full left-0 w-full bg-white border border-gray-200 shadow-md max-h-40 overflow-y-auto rounded-md mt-1">
                        {pincodeSuggestions.map((item, idx) => (
                          <li
                            key={item.pincode + '-' + idx}
                            onClick={() => handleSelectPincode(item)}
                            className="px-3 py-2 cursor-pointer hover:bg-gray-100 border-b border-gray-100 last:border-b-0 text-[13px]"
                          >
                            <span className="font-medium text-gray-800">{item.pincode}</span>
                            <span className="text-gray-500 ml-2">- {item.area}, {item.city}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  <div ref={cityRef} className="relative w-full">
                    <Input
                      name="patient.city"
                      value={values?.patient?.city}
                      onChange={handleCityChange}
                      placeholder="Enter City"
                      type="text"
                      labelName="City"
                      labelClassName="block text-primary-400 text-[13px] leading-[20px]"
                      className="border-none bg-transparent shadow-none outline-none placeholder:text-gray-400 w-full h-[24px]"
                      inputBox={getPendingStyle(isCityFilled, touched.patient?.city && errors.patient?.city)}
                      isError={
                        touched.patient?.city && Boolean(errors.patient?.city)
                      }
                      errorMessage={errors.patient?.city}
                    />
                    <button
                      type="button"
                      onClick={() => setCityMode(false)}
                      className={`absolute right-2 top-1/2 -translate-y-1/2 h-8 px-2 rounded-[4px] bg-primary-50 hover:bg-primary-100 text-[11px] text-primary-600 font-medium transition-colors border border-primary-100 flex items-center gap-1 ${readOnly ? 'opacity-80' : ''}`}
                    >
                      Swap to Pincode
                    </button>
                    {showCitySuggestions && (
                      <ul className="absolute z-[9999] top-full left-0 w-full bg-white border border-gray-200 shadow-md max-h-40 overflow-y-auto rounded-md mt-1">
                        {citySuggestions.map((item, idx) => (
                          <li
                            key={item.city + '-' + item.state + '-' + idx}
                            onClick={() => handleSelectCity(item)}
                            className="px-3 py-2 cursor-pointer hover:bg-gray-100 border-b border-gray-100 last:border-b-0 text-[13px]"
                          >
                            <span className="font-medium text-gray-800">{item.city}</span>
                            <span className="text-gray-500 ml-2">({item.state})</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              <div className="col-span-12 md:col-span-6">
                {!ageMode ? (
                  <div className="relative w-full">
                    <Input
                      name="patient.dateofbirth"
                      type="date"
                      value={values?.patient?.dateofbirth}
                      placeholder="DD MM YYYY"
                      dateFormat="dd-MM-yyyy"
                      maxDate={new Date()}
                      clearIcon={null}
                      labelName="Date of Birth"
                      labelClassName="block text-primary-400 text-[13px] leading-[20px]"
                      className="border-none bg-transparent shadow-none outline-none placeholder:text-gray-400 w-full h-[24px]"
                      inputBox={getPendingStyle(isDobFilled, touched.patient?.dateofbirth && errors.patient?.dateofbirth)}
                      onChange={(date) => {
                        setFieldValue("patient.dateofbirth", date);
                        setFieldValue("patient.age", "");
                      }}
                      isError={
                        touched.patient?.dateofbirth && Boolean(errors.patient?.dateofbirth)
                      }
                      errorMessage={errors.patient?.dateofbirth}
                    />
                    <button
                      type="button"
                      onClick={() => setAgeMode(true)}
                      className={`absolute right-2 top-1/2 -translate-y-1/2 h-8 px-2 rounded-[4px] bg-primary-50 hover:bg-primary-100 text-[11px] text-primary-600 font-medium transition-colors border border-primary-100 flex items-center gap-1 ${readOnly ? 'opacity-80' : ''}`}
                    >
                      Swap to Age
                    </button>
                  </div>
                ) : (
                  <div className="relative w-full">
                    <Input
                      name="patient.age"
                      value={values?.patient?.age}
                      onChange={(e) => {
                        setFieldValue("patient.age", e.target.value);
                        setFieldValue("patient.dateofbirth", "");
                      }}
                      placeholder="e.g. 54"
                      type="number"
                      labelName="Age"
                      labelClassName="block text-primary-400 text-[13px] leading-[20px]"
                      className="border-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none bg-transparent shadow-none outline-none placeholder:text-gray-400 w-full h-[24px]"
                      inputBox={getPendingStyle(isAgeFilled, touched.patient?.age && errors.patient?.age)}
                      isError={
                        touched.patient?.age && Boolean(errors.patient?.age)
                      }
                      errorMessage={errors.patient?.age}
                    />
                    <button
                      type="button"
                      onClick={() => setAgeMode(false)}
                      className={`absolute right-2 top-1/2 -translate-y-1/2 h-8 px-2 rounded-[4px] bg-primary-50 hover:bg-primary-100 text-[11px] text-primary-600 font-medium transition-colors border border-primary-100 flex items-center gap-1 ${readOnly ? 'opacity-80' : ''}`}
                    >
                      Swap to DoB
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(PatientOrderDetail);
